import apisauce from 'apisauce'
import { WebAuth } from 'auth0-js'
import I18n from 'APP/I18n'

import Config from 'APP/Config'
import type { LanguageMap } from 'APP/Config/types'
import { getHref } from 'APP/Lib/Utilities'
import { logger } from 'APP/Lib/Datadog'
import Analytics from 'APP/Services/Analytics'
import {
  createUserInfoCache,
  getCachedUserInfo,
  normalizeUserInfoData,
  UserInfoCache,
  UserInfoData,
} from 'APP/Services/Auth/UserInfoCache'

function getUILocales(): string {
  return (
    Config.AUTH0_LANGUAGE_LOCALES![I18n.locale as keyof LanguageMap] ||
    Config.AUTH0_LANGUAGE_LOCALES!.en
  )
}

type AuthCallbackResult<T = unknown> = (err: unknown, res: unknown) => void | T

type AuthorizeParams = {
  connection?: string | null
  triggerSilentlogin?: boolean
  loginHint?: string | null
  skipLoginPrompt?: boolean
}

type SignupParams = {
  connection?: string | null
  triggerSilentlogin?: boolean
}

type GetUserInfoParams = {
  accessToken: string
  forceRefresh?: boolean
}

type LoginWithUsernameAndPasswordParams = {
  username: string
  password: string
  redirectUri?: string
}

type LoginWithExchangeTokenParams = {
  subjectToken: string
}

type TokenExchangeData = {
  access_token?: string
  refresh_token?: string
  id_token?: string
  expires_in?: number
  error?: unknown
  errors?: unknown
  error_description?: unknown
}

class AuthClient {
  userInfoCache: UserInfoCache = null
  baseURL?: string

  init() {
    this.clearUserInfoCache()
    this.baseURL = Config.AUTH0_WEB_URL
  }

  get api() {
    return apisauce.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 50000,
    })
  }

  get auth0() {
    return new WebAuth({
      domain: Config.AUTH0_WEB_DOMAIN,
      clientID: Config.AUTH0_WEB_CLIENT_ID,
      redirectUri: `${window.location.href}?lng=${I18n.locale}`,
      responseType: 'token',
      audience: Config.AUTH0_API_AUDIENCE,
      scope: 'openid offline_access app_metadata email',
    })
  }

  // Returns Auth0 params that differ between federated (SSO) and standard Dialogue logins.
  // Federated: omit brand_id/acr_values/prompt — they are Dialogue-specific and not accepted
  // by the IdP. Omitting prompt lets Auth0 redirect to the IdP unconditionally; if the member
  // has an active session there they land back silently, otherwise the IdP shows its login page.
  // Standard: include brand_id/acr_values. omitDialogueParamsOnSilent skips them for a silent
  // authorize but not for signup.
  getFederatedOrStandardParams = ({
    connection,
    triggerSilentlogin,
    omitDialogueParamsOnSilent = false,
    skipLoginPrompt = false,
  }: {
    connection?: string | null
    triggerSilentlogin?: boolean
    omitDialogueParamsOnSilent?: boolean
    skipLoginPrompt?: boolean
  }): Record<string, unknown> => {
    if (connection) {
      return { connection }
    }
    if (omitDialogueParamsOnSilent && triggerSilentlogin) {
      return { prompt: 'none' }
    }
    const prompt = triggerSilentlogin ? 'none' : skipLoginPrompt ? undefined : 'login'
    return {
      brand_id: Config.BRAND_ID,
      acr_values: 'http://schemas.openid.net/pape/policies/2007/06/multi-factor',
      ...(prompt !== undefined ? { prompt } : {}),
    }
  }

  authorize = ({
    connection = null,
    triggerSilentlogin = false,
    loginHint = null,
    skipLoginPrompt = false,
  }: AuthorizeParams) => {
    // Only send connection when set (via getFederatedOrStandardParams); a null value
    // serializes as an empty `connection=` which makes Auth0 resolve a connection
    // named "" and fail with server_error.
    const options: Record<string, unknown> = {
      ...this.getFederatedOrStandardParams({
        connection,
        triggerSilentlogin,
        omitDialogueParamsOnSilent: true,
        skipLoginPrompt,
      }),
      ui_locales: getUILocales(),
      audience: Config.AUTH0_API_AUDIENCE,
      scope: 'openid offline_access app_metadata email',
      ...(loginHint ? { login_hint: loginHint, 'ext-email-readonly': 'true' } : {}),
    }

    // Add ephemeral multipass PR number if configured (development only)
    if (Config.MULTIPASS_PR) {
      options.multipass_pr_number = Config.MULTIPASS_PR
    }

    return new Promise((resolve, reject) => {
      const callback: AuthCallbackResult = (err, res) => {
        //Ignoring coverage because testing this callback would test nothing
        /* istanbul ignore next */
        if (err) {
          reject(err)
        } else {
          resolve(res)
        }
      }
      this.auth0.authorize(options, callback)
    })
  }

  renewAuth = () => {
    const options: Record<string, unknown> = { brand_id: Config.BRAND_ID }

    // Add ephemeral multipass PR number if configured (development only)
    if (Config.MULTIPASS_PR) {
      options.multipass_pr_number = Config.MULTIPASS_PR
    }

    return new Promise((resolve, reject) => {
      this.auth0.checkSession(options, (err, res) => {
        if (err) {
          reject(err)
        } else {
          resolve(res)
        }
      })
    })
  }

  logout = () => {
    this.clearUserInfoCache()

    return new Promise((resolve, reject) => {
      this.auth0.logout({ returnTo: getHref() }, (err, res) => {
        if (err) {
          reject(err)
        } else {
          resolve(res)
        }
      })
    })
  }

  signup = ({ connection = null, triggerSilentlogin = false }: SignupParams) => {
    const options: Record<string, unknown> = {
      screen_hint: 'signup',
      ...this.getFederatedOrStandardParams({ connection, triggerSilentlogin }),
      ui_locales: getUILocales(),
      audience: Config.AUTH0_API_AUDIENCE,
      scope: 'openid offline_access app_metadata email',
      ...Analytics.getUtmParamsForSignup(),
    }

    // Add ephemeral multipass PR number if configured (development only)
    if (Config.MULTIPASS_PR) {
      options.multipass_pr_number = Config.MULTIPASS_PR
    }

    return new Promise((resolve, reject) => {
      this.auth0.authorize(options, (err, res) => {
        //Ignoring coverage because testing this callback would test nothing
        /* istanbul ignore next */
        if (err) {
          reject(err)
        } else {
          resolve(res)
        }
      })
    })
  }

  clearUserInfoCache = () => {
    this.userInfoCache = null
  }

  /*
    Method to extract custom claims and mfa_enabled

    @deprecated - Moving forward please use loginState.customClaims for custom claims (email_verified, historically_eligible_brands)
  */
  getUserInfo = async ({ accessToken, forceRefresh = false }: GetUserInfoParams) => {
    const cachedUserInfo = getCachedUserInfo({
      accessToken,
      forceRefresh,
      userInfoCache: this.userInfoCache,
    })

    if (cachedUserInfo) {
      return cachedUserInfo
    }

    let response

    try {
      response = await this.api.get<UserInfoData>(
        '/userinfo',
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      )
    } catch (e) {
      throw new Error('Unable to get user info due to API error', {
        cause: {
          message: (e as Error).message,
          cause: (e as Error).cause,
        },
      })
    }

    if (!response.ok) {
      const errDescription = response.data?.error_description
      const errDetails = response.data?.error || response.data?.errors
      logger.error(
        'Error getting user info from Auth0',
        {
          data: response.data,
          description: errDescription,
        },
        errDetails
      )

      throw new Error('Unable to get user info due to client error', {
        cause: {
          error: errDetails,
          description: errDescription,
        },
      })
    }

    const userInfo = normalizeUserInfoData(response.data)
    this.userInfoCache = createUserInfoCache({ accessToken, data: userInfo })

    return userInfo
  }

  loginWithUsernameAndPassword = ({
    username,
    password,
    redirectUri,
  }: LoginWithUsernameAndPasswordParams) => {
    const payload: Record<string, unknown> = {
      username,
      password,
      realm: Config.AUTH0_CONNECTION,
      brand_id: Config.BRAND_ID,
      ui_locales: getUILocales(),
    }
    if (redirectUri) {
      payload.redirectUri = redirectUri
    }
    return new Promise((resolve, reject) => {
      this.auth0.login(
        payload,
        /* istanbul ignore next */
        (err, res) => {
          //Ignoring coverage because testing this callback would test nothing
          /* istanbul ignore next */
          if (err) {
            reject(err)
          } else {
            resolve(res)
          }
        }
      )
    })
  }

  loginWithExchangeToken = async ({ subjectToken }: LoginWithExchangeTokenParams) => {
    let response

    try {
      response = await this.api.post<TokenExchangeData>('oauth/token', {
        grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
        client_id: Config.AUTH0_WEB_CLIENT_ID,
        subject_token_type: Config.AUTH0_SUBJECT_TOKEN_TYPE,
        subject_token: subjectToken,
        scope: 'openid offline_access app_metadata email',
        audience: Config.AUTH0_API_AUDIENCE,
        brand_id: Config.BRAND_ID,
        ui_locales: getUILocales(),
      })
    } catch (e) {
      throw new Error('Unable to perform token exchange due to API error', {
        cause: {
          message: (e as Error).message,
          cause: (e as Error).cause,
        },
      })
    }

    if (!response.ok) {
      throw new Error('Unable to perform token exchange due to client error', {
        cause: {
          error: response.data?.error || response.data?.errors,
          description: response.data?.error_description,
        },
      })
    }

    return {
      accessToken: response.data?.access_token,
      refreshToken: response.data?.refresh_token,
      idToken: response.data?.id_token,
      expiresIn: response.data?.expires_in,
      ...response,
    }
  }

  parseHash = () => {
    return new Promise((resolve, reject) => {
      this.auth0.parseHash({ hash: window.location.hash }, (err, res) => {
        if (err) {
          reject(err)
        } else {
          resolve(res)
        }
      })
    })
  }
}

const authClient = new AuthClient()
Config.onConfigKeyChange('AUTH0_WEB_URL', (done) => {
  authClient.init()
  done()
})
Config.onConfigKeyChange('AUTH0_WEB_CLIENT_ID', (done) => {
  authClient.init()
  done()
})
authClient.init()

export default authClient
